/**
 * Who a generated document says it comes from — the demo's `BRAND` and
 * `ORGS` blocks (demo line 1475), split by what follows the brand switch.
 *
 * ## Two kinds of identity
 *
 * - **The OEM** — the letterhead, the footer's legal line, the deck cover and
 *   the OEM's own mail signature. This follows the selected brand, the same
 *   way `orgLabel()` swaps `{brand}` into a persona's organisation. Meridian
 *   keeps every field the demo had; Komatsu carries its trading name and the
 *   division line and nothing else. An ABN, a street address or a switchboard
 *   number for a real company would be invented, so those fields are `null`
 *   and the components leave them out.
 * - **The customers** — the six mining companies and their contractors. They
 *   are fictional in the original and stay fictional here, whichever brand is
 *   selected, because the fleet data is theirs.
 *
 * Pure data. The one reader that needs the live brand is `useOemDocIdentity()`
 * in `./useOemDocIdentity.ts`; transforms take an identity as an argument.
 *
 * @see src/versions/v1/shared/BrandContext.tsx — the brand registry
 * @see docs/logic/reports/overview.md — where the letterhead kit is used
 */

import type { BrandId } from '../BrandContext';

/** The OEM as a document names it. */
export interface OemDocIdentity {
  /** Trading name — "Meridian Equipment", "Komatsu". */
  readonly name: string;
  /** The name in running text — "Meridian machines", "every Komatsu WO". */
  readonly short: string;
  /** Registered name, printed bold on the letterhead. */
  readonly legal: string;
  /** Business unit under the legal name. */
  readonly division: string;
  /** Short unit name for a mail signature. */
  readonly unit: string;
  readonly abn: string | null;
  readonly hq: string | null;
  /** Street address for a mail signature — `hq` without the country. */
  readonly addr: string | null;
  /** Used where a person has no site of their own. */
  readonly hqShort: string | null;
  readonly phone: string | null;
  /** The 24/7 line, without the parenthetical. */
  readonly opsCentre: string | null;
  readonly web: string | null;
}

/** The product that generated the document — the same under every brand. */
export const OEM_PRODUCT = {
  name: 'AiRis ProjeX OEM+',
  desc: 'Fleet service workspace',
} as const;

/** Demo line 1475, `BRANDJ.brand`, per brand. */
export const OEM_DOC_IDENTITY: Readonly<Record<BrandId, OemDocIdentity>> = {
  meridian: {
    name: 'Meridian Equipment',
    short: 'Meridian',
    legal: 'Meridian Equipment Pty Ltd',
    division: 'Mining Division — Australia',
    unit: 'Mining Division',
    abn: '51 550 975 004',
    hq: '18 Ironbark Way, Kewdale WA 6105, Australia',
    addr: '18 Ironbark Way, Kewdale WA 6105',
    hqShort: 'Kewdale WA',
    phone: '+61 8 5550 1400',
    opsCentre: '1800 975 640',
    web: 'meridian-eq.example',
  },
  komatsu: {
    name: 'Komatsu',
    short: 'Komatsu',
    legal: 'Komatsu Ltd',
    division: 'Mining Division — Australia',
    unit: 'Mining Division',
    abn: null,
    hq: null,
    addr: null,
    hqShort: null,
    phone: null,
    opsCentre: null,
    web: null,
  },
};

/** The name the demo's data files use for the OEM. */
export const DATA_OEM_ORG = 'Meridian Equipment';

/** A customer organisation's signature details — demo `ORGS[org]`. */
export interface CustomerOrg {
  readonly legal: string;
  readonly unit: string;
  readonly addr: string;
  readonly phone: string;
  readonly web: string;
}

/** Demo line 1475, `BRANDJ.orgs`, less the OEM. All fictional. */
export const CUSTOMER_ORGS: Readonly<Record<string, CustomerOrg>> = {
  'Kalbarri Gold': {
    legal: 'Kalbarri Gold Pty Ltd',
    unit: 'Kalbarri Gold Operations — Underground',
    addr: 'Kalbarri Gold Mine, Murchison WA',
    phone: '(08) 5550 2200',
    web: 'kalbarrigold.example',
  },
  'Broken Ridge': {
    legal: 'Broken Ridge Metals Ltd',
    unit: 'Broken Ridge Mine — Maintenance',
    addr: 'Broken Ridge Mine, Cobar Basin NSW',
    phone: '(02) 5550 3300',
    web: 'brokenridge.example',
  },
  'Mount Isolde Copper': {
    legal: 'Mount Isolde Copper Pty Ltd',
    unit: 'Mount Isolde Mine',
    addr: 'Mount Isolde Mine, North West QLD',
    phone: '(07) 5550 4400',
    web: 'mtisolde.example',
  },
  'Redgum Mining Services': {
    legal: 'Redgum Mining Services Pty Ltd',
    unit: 'Mount Isolde underground contract',
    addr: 'Mount Isolde Mine, North West QLD',
    phone: '(07) 5550 4500',
    web: 'redgum.example',
  },
  'Ochre Hill Gold': {
    legal: 'Ochre Hill Gold Pty Ltd',
    unit: 'Ochre Hill Open Pit — Drill & Blast',
    addr: 'Ochre Hill Mine, Barkly NT',
    phone: '(08) 5550 7100',
    web: 'ochrehill.example',
  },
  'Gawler Deep': {
    legal: 'Gawler Deep Mining Ltd',
    unit: 'Gawler Deep Operations — Asset Management',
    addr: 'Gawler Deep Mine, Gawler Craton SA',
    phone: '(08) 5550 6600',
    web: 'gawlerdeep.example',
  },
  'Spinifex Underground': {
    legal: 'Spinifex Underground Contracting Pty Ltd',
    unit: 'Gawler Deep northern workings contract',
    addr: 'Gawler Deep Mine, Gawler Craton SA',
    phone: '(08) 5550 6700',
    web: 'spinifexug.example',
  },
  'Mount Read Metals': {
    legal: 'Mount Read Metals Pty Ltd',
    unit: "Mount Read Underground — Owner's Team",
    addr: 'Mount Read Mine, West Coast TAS',
    phone: '(03) 5550 8100',
    web: 'mountreadmetals.example',
  },
  'Western Tiers Mining': {
    legal: 'Western Tiers Mining Services Pty Ltd',
    unit: 'Mount Read underground mining contract',
    addr: 'Mount Read Mine, West Coast TAS',
    phone: '(03) 5550 8200',
    web: 'westerntiers.example',
  },
};

/** Demo `BRAND.mobiles` — the OEM staff mobiles the signatures print. Fictional ranges. */
export const OEM_MOBILES: Readonly<Record<string, string>> = {
  'Lena Sjoberg': '0491 570 110',
  'Hanna Virtanen': '0491 570 123',
  'Tom Reilly': '0491 570 138',
  'Grace Ndlovu': '1800 975 640',
  'Dale Whitmore': '0491 570 156',
  'Ben Tuilagi': '0491 570 159',
  'Anders Lindqvist': '0491 570 163',
  'Kieran Walsh': '0491 570 171',
  'Lucas Ferreira': '0491 570 174',
  'Noah Petrov': '0491 570 177',
  'Amelia Hart': '0491 570 181',
  'Hamish Cameron': '0491 570 186',
  'Zoe Lindgren': '0491 570 189',
  'Priya Raman': '0491 570 192',
  'Matteo Rossi': '0491 570 195',
};

/**
 * Demo line 1491 — the legal line of the footer, `legal · ABN · HQ`, with the
 * fields a brand does not carry dropped rather than printed blank.
 */
export function legalLine(oem: OemDocIdentity): string {
  return [oem.legal, oem.abn ? `ABN ${oem.abn}` : null, oem.hq].filter(Boolean).join(' · ');
}

/**
 * Demo line 1493 — one line of the OEM's mail signature:
 * `legal · unit · address · phone · web`.
 */
export function oemSignatureLine(oem: OemDocIdentity): string {
  return [oem.legal, oem.unit, oem.addr, oem.phone, oem.web].filter(Boolean).join(' · ');
}

/** Demo line 1493 — a customer's signature line, or `null` for an unknown org. */
export function customerSignatureLine(org: string): string | null {
  const o = CUSTOMER_ORGS[org];
  return o ? [o.legal, o.unit, o.addr, o.phone, o.web].join(' · ') : null;
}
